import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  ButtonDS,
  DownloadOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  sortByDateTime,
  triggerToast,
  usePersistMuiTableState,
} from "@qivia/ui";
import { PublicInvoices, PublicInvoicesDisplayed } from "../billingsAPI";
import {
  selectInvoicesList,
  selectInvoicesListStatus,
  invoicesListAsync,
  invoiceExportAsync,
  selectInvoiceExportLink,
  documentDownloadAsync,
  selectInvoiceExportStatus,
} from "../billingsSlice";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { selectCompany } from "../../homeSlice";
import "../../../../i18n/i18n";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { formatDate, getDate } from "../../../../libSlice/materialUiFormats";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";

export const InvoiceTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const company = useAppSelector(selectCompany);

  const [invoicesListSorted, setInvoicesListSorted] = useState<
    PublicInvoicesDisplayed[] | null
  >([]);
  const invoicesListStatus = useAppSelector(selectInvoicesListStatus);
  const invoicesListInit: PublicInvoices[] = useAppSelector(selectInvoicesList);
  const invoiceExportLink = useAppSelector(selectInvoiceExportLink);
  const invoicesExportStatus = useAppSelector(selectInvoiceExportStatus);

  const pageName = "invoices";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      totalAmount: "avg",
      fileName: "size",
      date: "size",
      dueDate: "size",
      bucketFileNameDownload: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const invoicesList: PublicInvoices[] = useMemo(() => {
    const rowsWithId = invoicesListInit.map((invoice) => {
      const newDict: PublicInvoices & { id: string } = {
        ...invoice,
        id: invoice.number,
      };
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc");
  }, [invoicesListInit]);

  useEffect(() => {
    if (company && invoicesListStatus === "idle") {
      void dispatch(invoicesListAsync(company.uuid));
    }
  }, [dispatch, company, invoicesListStatus]);

  useEffect(() => {
    if (invoicesExportStatus === "success") {
      triggerToast(t("billings.invoices.upload.success") || "", "valid");
    } else if (invoicesExportStatus === "failed") {
      triggerToast(t("billings.invoices.upload.failure") || "", "error");
    }
  }, [invoicesExportStatus, t]);

  useEffect(() => {
    if (invoicesList && invoicesListStatus === "success") {
      const invoicesListSorted = sortByDateTime(invoicesList, "desc");
      setInvoicesListSorted(invoicesListSorted);
    }
  }, [invoicesList, invoicesListStatus]);

  useEffect(() => {
    if (invoiceExportLink.presignedUrl && invoiceExportLink.fileName) {
      void dispatch(documentDownloadAsync(invoiceExportLink));
    }
  }, [invoiceExportLink, dispatch]);

  const columnsDisplayed: {
    [index in keyof PublicInvoicesDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      totalAmount: {
        type: "number",
        width: 100,
        valueFormatter: (value: number) => {
          return (
            <StyledAmount>
              {value ? value.toFixed(2) + " € TTC" : "0 € TTC"}
              <Spacer x={0.75} />
            </StyledAmount>
          );
        },
      },
      date: {
        type: "date",
        width: 200,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      dueDate: {
        type: "date",
        width: 200,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      fileName: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => value,
      },
      bucketFileNameDownload: {
        type: "string",
        width: 200,
        valueFormatter: (value: string, row) => {
          return (
            <StyledColumnButton>
              <ButtonDS
                sizeButton={"S"}
                format={"hug"}
                buttonType={"secondary"}
                singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                onClick={() => {
                  void dispatch(
                    invoiceExportAsync({
                      bucketFileName: value ?? "",
                      fileName:
                        "fileName" in row ? (row["fileName"] as string) : "",
                    }),
                  );
                }}
              />
            </StyledColumnButton>
          );
        },
      },
    };
  }, [dispatch]);

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("invoices.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  if (params.tab !== "invoices") {
    return;
  }

  const searchBarProps = {
    values: invoicesList,
    setFilterValues: setInvoicesListSorted,
    keysToIgnore: ["bucketFileNameDownload" as const],
  };

  if (!invoicesListSorted) return <LaunchPage hasBorderRadius={true} />;

  return (
    <MaterialUiTable<keyof PublicInvoicesDisplayed, PublicInvoicesDisplayed>
      rows={invoicesListSorted}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};

const StyledAmount = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;
